import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminMilestoneService from '../../services/AdminMilestoneService';
import { toast } from 'react-hot-toast';
import {
  Users,
  CheckCircle,
  AlertCircle,
  Clock,
  Play,
  Eye,
  Loader2,
  Search,
  Filter,
  Send,
  ChevronDown,
  ChevronUp,
  Trash2
} from 'lucide-react';

export default function MilestoneGenerationAdmin() {
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState({
    totalStudents: 0,
    studentsWithMilestones: 0,
    totalMilestones: 0,
    missingMilestones: []
  });

  // Template Push Feature States
  const [templates, setTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState('');
  const [templatePhaseFilter, setTemplatePhaseFilter] = useState('');
  const [showTemplatePush, setShowTemplatePush] = useState(false);
  const [pushingTemplates, setPushingTemplates] = useState(false);

  // Delete Feature States
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteType, setDeleteType] = useState('milestone'); // 'milestone' or 'all'
  const [deletePreview, setDeletePreview] = useState(null); // Store task counts for confirmation

  useEffect(() => {
    loadStudents();
    loadStats();
    loadTemplates();
  }, []);

  const loadStudents = async () => {
    try {
      console.log('[MilestoneGenerationAdmin] Loading students...');
      setLoading(true);
      
      // Get all students from student_academic_profiles
      const { data: academicProfiles, error: profileError } = await supabase
        .from('student_academic_profiles')
        .select('user_id, graduation_year, gpa_weighted, sat_total, act_composite')
        .order('user_id');
      
      if (profileError) throw profileError;
      
      // Get user details from family_members
      const userIds = (academicProfiles || []).map(p => p.user_id);
      const { data: familyMembers, error: familyError } = await supabase
        .from('family_members')
        .select('id, name, email')
        .in('id', userIds);
      
      if (familyError) throw familyError;
      
      // Create a map for easy lookup
      const userMap = (familyMembers || []).reduce((acc, member) => {
        acc[member.id] = member;
        return acc;
      }, {});
      
      // Get milestone counts for each student
      const studentData = await Promise.all(
        (academicProfiles || []).map(async (profile) => {
          const user = userMap[profile.user_id] || {};
          
          const { count, error: countError } = await supabase
            .from('timeline_items')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', profile.user_id)
            .in('source_type', ['milestone_template', 'milestone_subtask']);

          if (countError) {
            console.error(`[MilestoneGenerationAdmin] Error counting milestones for user ${profile.user_id}:`, countError);
          }
          
          const { data: colleges } = await supabase
            .from('college_journey_colleges')
            .select('id, name')
            .eq('user_id', profile.user_id);
          
          const result = {
            id: profile.user_id,
            full_name: user.name || 'Unknown',
            email: user.email || '',
            graduation_year: profile.graduation_year,
            milestoneCount: count || 0,
            collegeCount: colleges?.length || 0,
            colleges: colleges || []
          };

          console.log(`[MilestoneGenerationAdmin] Student ${profile.user_id} has ${count || 0} milestones`);
          return result;
        })
      );
      
      console.log(`[MilestoneGenerationAdmin] Loaded ${studentData.length} students`);
      setStudents(studentData);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      console.log('[MilestoneGenerationAdmin] Loading stats...');
      // Get overall statistics - students are those with academic profiles
      const { data: allStudents } = await supabase
        .from('student_academic_profiles')
        .select('user_id');

      const { data: studentsWithMilestones } = await supabase
        .from('timeline_items')
        .select('user_id')
        .in('source_type', ['milestone_template', 'milestone_subtask'])
        .not('user_id', 'is', null);

      const uniqueStudentsWithMilestones = new Set(
        studentsWithMilestones?.map(item => item.user_id) || []
      );

      const { count: totalMilestones } = await supabase
        .from('timeline_items')
        .select('*', { count: 'exact', head: true })
        .in('source_type', ['milestone_template', 'milestone_subtask']);

      // Find students missing milestones
      const missingMilestones = allStudents?.filter(
        student => !uniqueStudentsWithMilestones.has(student.user_id)
      ) || [];

      const newStats = {
        totalStudents: allStudents?.length || 0,
        studentsWithMilestones: uniqueStudentsWithMilestones.size,
        totalMilestones: totalMilestones || 0,
        missingMilestones
      };

      console.log('[MilestoneGenerationAdmin] Stats loaded:', newStats);
      setStats(newStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('milestone_templates')
        .select('*')
        .eq('is_active', true)
        .order('category')
        .order('phase')
        .order('title');

      if (error) throw error;
      setTemplates(data || []);
      console.log(`[MilestoneGenerationAdmin] Loaded ${data?.length || 0} templates`);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load milestone templates');
    }
  };

  const previewGeneration = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    
    try {
      setLoading(true);
      const previewData = [];
      
      for (const studentId of selectedStudents) {
        const student = students.find(s => s.id === studentId);
        
        // Determine student's timeline urgency
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        const graduationYear = student?.graduation_year || (currentYear + 1);
        
        // Determine if student is on a rush timeline
        const isRushTimeline = (
          (graduationYear === currentYear) || // Graduating this year
          (graduationYear === currentYear + 1 && currentMonth >= 8) // Senior in fall/winter
        );
        
        // Get templates that would be generated from ALL phases
        const phasesToInclude = ['exploration', 'preparation', 'application', 'decision'];
        const categories = ['application', 'essay', 'testing', 'financial_aid', 'profile', 'research'];
        let totalToGenerate = 0;
        let filteredCount = 0;
        const categoryBreakdown = {};
        const phaseBreakdown = {};
        
        for (const phase of phasesToInclude) {
          let phaseTotal = 0;
          
          for (const category of categories) {
            const { data: templates } = await supabase
              .from('milestone_templates')
              .select('id, time_sensitive')
              .eq('category', category)
              .eq('phase', phase)
              .eq('is_active', true);
            
            if (!templates || templates.length === 0) continue;
            
            // Filter out time-sensitive tasks for rush timeline students
            const filteredTemplates = templates.filter(template => {
              if (isRushTimeline && template.time_sensitive) {
                filteredCount++;
                return false;
              }
              return true;
            });
            
            const count = filteredTemplates.length;
            if (count > 0) {
              categoryBreakdown[category] = (categoryBreakdown[category] || 0) + count;
              phaseTotal += count;
            }
            totalToGenerate += count;
          }
          
          if (phaseTotal > 0) {
            phaseBreakdown[phase] = phaseTotal;
          }
        }
        
        previewData.push({
          studentId,
          studentName: student?.full_name || 'Unknown',
          isRushTimeline,
          estimatedMilestones: totalToGenerate,
          categoryBreakdown,
          phaseBreakdown,
          filteredCount,
          colleges: student?.colleges || []
        });
      }
      
      setPreview(previewData);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const generateGlobalMilestones = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    
    try {
      setGenerating(true);
      const results = {
        success: 0,
        failed: 0,
        totalGenerated: 0,
        errors: []
      };
      
      for (const studentId of selectedStudents) {
        try {
          console.log(`[MilestoneGenerationAdmin] Generating milestones for student ${studentId}`);
          // Generate general milestones using admin service
          const generalResult = await AdminMilestoneService.generateMilestonesForStudent(studentId);
          console.log(`[MilestoneGenerationAdmin] Student ${studentId} result:`, generalResult);
          results.totalGenerated += generalResult.total;
          results.success++;
        } catch (error) {
          console.error(`[MilestoneGenerationAdmin] Error for student ${studentId}:`, error);
          results.failed++;
          results.errors.push({
            studentId,
            error: error.message
          });
        }
      }

      console.log('[MilestoneGenerationAdmin] Final results:', results);

      if (results.totalGenerated > 0) {
        console.log('[MilestoneGenerationAdmin] Showing success toast');
        try {
          toast.success(`Global tasks generated: ${results.totalGenerated} tasks for ${results.success} students (FAFSA, transcripts, Common App, etc.)`);
          console.log('[MilestoneGenerationAdmin] Toast.success called successfully');
        } catch (toastError) {
          console.error('[MilestoneGenerationAdmin] Toast error:', toastError);
        }
      } else {
        console.log('[MilestoneGenerationAdmin] Showing info toast - no new milestones');
        try {
          toast(`No new global tasks generated - students may already have existing tasks`, {
            icon: 'ℹ️',
            duration: 4000,
          });
          console.log('[MilestoneGenerationAdmin] Toast called successfully');
        } catch (toastError) {
          console.error('[MilestoneGenerationAdmin] Toast error:', toastError);
        }
      }
      
      if (results.errors.length > 0) {
        console.error('Generation errors:', results.errors);
        toast.error(`${results.failed} students had errors`);
      }
      
      console.log('[MilestoneGenerationAdmin] Refreshing stats and students...');
      // Add longer delay and retry logic to ensure database transactions have committed
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Direct database verification before showing stats
      console.log('[MilestoneGenerationAdmin] Verifying milestone creation in database...');
      for (const studentId of selectedStudents) {
        // Query 1: Count milestone items (template + subtask)
        const { count, error } = await supabase
          .from('timeline_items')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', studentId)
          .in('source_type', ['milestone_template', 'milestone_subtask']);
        
        console.log(`[MilestoneGenerationAdmin] Direct DB query: Student ${studentId} has ${count} milestone items (template + subtask)`);
        
        // Query 2: Get actual records to see what exists
        const { data: records, error: recordsError } = await supabase
          .from('timeline_items')
          .select('source_type, source_id, title, created_at, metadata')
          .eq('user_id', studentId)
          .order('created_at', { ascending: false })
          .limit(5);
        
        console.log(`[MilestoneGenerationAdmin] Recent records for ${studentId}:`, records?.map(r => ({ 
          source_type: r.source_type, 
          title: r.title, 
          created_at: r.created_at,
          generated_by: r.metadata?.generated_by 
        })));
        
        // Query 3: Count ALL timeline items for this student
        const { count: totalCount } = await supabase
          .from('timeline_items')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', studentId);
        
        console.log(`[MilestoneGenerationAdmin] Student ${studentId} has ${totalCount} total timeline items`);
        
        if (error || recordsError) {
          console.error('[MilestoneGenerationAdmin] DB verification errors:', { error, recordsError });
        }
      }
      
      // Retry stats loading with verification
      let retryCount = 0;
      const maxRetries = 3;
      let verificationPassed = false;
      
      while (retryCount < maxRetries && !verificationPassed) {
        await loadStats();
        await loadStudents();
        
        // Verify if any processed students now show milestones
        const processedStudents = students.filter(s => selectedStudents.includes(s.id));
        const studentsWithMilestones = processedStudents.filter(s => s.milestoneCount > 0);
        
        console.log(`[MilestoneGenerationAdmin] Stats check: ${studentsWithMilestones.length}/${processedStudents.length} students show milestones`);
        
        if (studentsWithMilestones.length > 0) {
          console.log('[MilestoneGenerationAdmin] Stats verification successful');
          verificationPassed = true;
          break;
        }
        
        retryCount++;
        if (retryCount < maxRetries) {
          console.log(`[MilestoneGenerationAdmin] Stats verification failed, retrying... (${retryCount}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      console.log('[MilestoneGenerationAdmin] Stats and students refreshed');
    } catch (error) {
      console.error('[MilestoneGenerationAdmin] Top-level error:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setGenerating(false);
      console.log('[MilestoneGenerationAdmin] Generation complete');
    }
  };

  const generateCollegeMilestonesDeadline = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    setGenerating(true);

    try {
      const results = {
        totalGenerated: 0,
        success: 0,
        errors: [],
        collegesProcessed: 0
      };

      for (const studentId of selectedStudents) {
        const student = students.find(s => s.id === studentId);
        console.log(`[MilestoneGenerationAdmin] Processing deadline generation for student: ${studentId} (${student?.full_name})`);

        try {
          // Generate deadline-based college-specific milestones if colleges exist
          if (student?.colleges && student.colleges.length > 0) {
            for (const college of student.colleges) {
              try {
                const collegeResult = await AdminMilestoneService.generateCollegeMilestones(
                  studentId,
                  college.id,
                  college.name,
                  { timing: 'with_deadlines', forceGenerate: true }
                );
                results.totalGenerated += collegeResult.total;
                results.collegesProcessed++;
              } catch (collegeError) {
                console.error(`Error generating deadline milestones for college ${college.name}:`, collegeError);
                results.errors.push(`${college.name}: ${collegeError.message}`);
              }
            }
          }
          
          results.success++;
          console.log(`[MilestoneGenerationAdmin] Deadline generation complete for student ${studentId}`);
        } catch (error) {
          console.error(`[MilestoneGenerationAdmin] Error for student ${studentId}:`, error);
          results.errors.push(`${student?.full_name}: ${error.message}`);
        }
      }

      if (results.errors.length > 0) {
        toast.error(`Generation completed with errors. Generated ${results.totalGenerated} milestones for ${results.collegesProcessed} colleges.`);
        console.error('Generation errors:', results.errors);
      } else {
        toast.success(`Successfully generated ${results.totalGenerated} deadline milestones for ${results.collegesProcessed} colleges across ${results.success} students.`);
      }

      // Reload stats after generation
      await loadStats();
      
    } catch (error) {
      console.error('[MilestoneGenerationAdmin] Error in deadline generation:', error);
      toast.error('Failed to generate deadline milestones: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const generateCollegeMilestones = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    
    try {
      setGenerating(true);
      const results = {
        success: 0,
        failed: 0,
        totalGenerated: 0,
        errors: [],
        collegesProcessed: 0
      };
      
      for (const studentId of selectedStudents) {
        try {
          const student = students.find(s => s.id === studentId);
          
          // Generate college-specific milestones with cross-college batching
          if (student?.colleges && student.colleges.length > 0) {
            try {
              console.log(`[MilestoneGenerationAdmin] Using cross-college batching for ${student.colleges.length} colleges`);
              
              const batchedResult = await AdminMilestoneService.generateImmediateCollegeMilestonesWithBatching(
                studentId,
                student.colleges,
                { forceGenerate: true }
              );
              
              results.totalGenerated += batchedResult.total;
              results.collegesProcessed += batchedResult.collegesProcessed;
              
              if (batchedResult.errors && batchedResult.errors.length > 0) {
                batchedResult.errors.forEach(error => {
                  results.errors.push({
                    studentId,
                    college: error.college,
                    error: error.error
                  });
                });
              }
              
            } catch (batchingError) {
              console.error(`Error in cross-college batching for student ${studentId}:`, batchingError);
              results.errors.push({
                studentId,
                error: batchingError.message,
                type: 'batching_error'
              });
            }
          }
          
          results.success++;
        } catch (error) {
          results.failed++;
          results.errors.push({
            studentId,
            error: error.message
          });
        }
      }

      if (results.totalGenerated > 0) {
        toast.success(`College tasks generated: ${results.totalGenerated} tasks for ${results.collegesProcessed} colleges (supplements, essays, visits, etc.)`);
      } else {
        toast(`No new college tasks generated - colleges may already have existing tasks`, {
          icon: 'ℹ️',
          duration: 4000,
        });
      }
      
      if (results.errors.length > 0) {
        console.error('College generation errors:', results.errors);
        toast.error(`${results.errors.length} college(s) had errors`);
      }
      
      console.log('[MilestoneGenerationAdmin] Refreshing stats after college generation...');
      // Add delay and retry logic for college generation too
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Retry stats loading with verification
      let retryCount = 0;
      const maxRetries = 3;
      while (retryCount < maxRetries) {
        await loadStats();
        await loadStudents();
        
        // Check if any students now have more milestones
        const updatedStudent = students.find(s => selectedStudents.includes(s.id));
        if (updatedStudent && updatedStudent.milestoneCount > 0) {
          console.log('[MilestoneGenerationAdmin] College stats verification successful');
          break;
        }
        
        retryCount++;
        if (retryCount < maxRetries) {
          console.log(`[MilestoneGenerationAdmin] College stats verification failed, retrying... (${retryCount}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
    } catch (error) {
      console.error('Error generating college milestones:', error);
      toast.error(`Failed to generate college milestones: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const selectAllMissing = () => {
    const missingStudentIds = students
      .filter(s => s.milestoneCount === 0)
      .map(s => s.id);
    setSelectedStudents(missingStudentIds);
  };

  const previewDelete = async (type) => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    try {
      const preview = {
        milestoneCount: 0,
        deadlineCount: 0,
        essayCount: 0,
        otherCount: 0,
        totalCount: 0,
        students: []
      };

      for (const studentId of selectedStudents) {
        const student = students.find(s => s.id === studentId);

        // Count milestone tasks (milestone_template + milestone_subtask)
        const { count: milestoneCount } = await supabase
          .from('timeline_items')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', studentId)
          .in('source_type', ['milestone_template', 'milestone_subtask']);

        // Count deadline reminder tasks
        const { count: deadlineCount } = await supabase
          .from('timeline_items')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', studentId)
          .eq('source_type', 'deadline_reminder');

        // Count essay tasks
        const { count: essayCount } = await supabase
          .from('timeline_items')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', studentId)
          .eq('source_type', 'essay_slot');

        // Count all other tasks
        const { count: otherCount } = await supabase
          .from('timeline_items')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', studentId)
          .not('source_type', 'in', '(milestone_template,milestone_subtask,deadline_reminder,essay_slot)');

        // Count total tasks
        const { count: totalCount } = await supabase
          .from('timeline_items')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', studentId);

        preview.milestoneCount += milestoneCount || 0;
        preview.deadlineCount += deadlineCount || 0;
        preview.essayCount += essayCount || 0;
        preview.otherCount += otherCount || 0;
        preview.totalCount += totalCount || 0;

        preview.students.push({
          id: studentId,
          name: student?.full_name || 'Unknown',
          milestoneCount: milestoneCount || 0,
          totalCount: totalCount || 0
        });
      }

      setDeletePreview(preview);
      setDeleteType(type);
      setShowDeleteConfirm(true);
    } catch (error) {
      console.error('[MilestoneGenerationAdmin] Error previewing delete:', error);
      toast.error('Failed to preview deletion counts');
    }
  };

  const deleteAllTasksForStudents = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    setDeleting(true);
    try {
      let totalDeleted = 0;
      const errors = [];

      for (const studentId of selectedStudents) {
        try {
          let query = supabase
            .from('timeline_items')
            .delete()
            .eq('user_id', studentId);

          // If deleting only milestone tasks, filter by source_type
          if (deleteType === 'milestone') {
            query = query.in('source_type', ['milestone_template', 'milestone_subtask']);
          }
          // If deleteType === 'all', delete everything (no additional filter)

          const { error, count } = await query;

          if (error) throw error;

          totalDeleted += count || 0;
          console.log(`[MilestoneGenerationAdmin] Deleted ${count || 0} tasks (${deleteType} mode) for student ${studentId}`);
        } catch (error) {
          console.error(`[MilestoneGenerationAdmin] Error deleting tasks for student ${studentId}:`, error);
          errors.push({
            studentId,
            error: error.message
          });
        }
      }

      const deleteTypeLabel = deleteType === 'milestone' ? 'milestone tasks' : 'tasks (including deadline reminders)';

      if (totalDeleted > 0) {
        toast.success(`Successfully deleted ${totalDeleted} ${deleteTypeLabel} for ${selectedStudents.length} student${selectedStudents.length !== 1 ? 's' : ''}`);
      } else {
        toast(`No ${deleteTypeLabel} found to delete for selected students`, {
          icon: 'ℹ️',
          duration: 4000,
        });
      }

      if (errors.length > 0) {
        console.error('Deletion errors:', errors);
        toast.error(`${errors.length} student(s) had errors during deletion`);
      }

      // Refresh stats and students
      await new Promise(resolve => setTimeout(resolve, 1000));
      await loadStats();
      await loadStudents();

      // Close confirmation dialog
      setShowDeleteConfirm(false);
      setDeletePreview(null);
    } catch (error) {
      console.error('[MilestoneGenerationAdmin] Error in task deletion:', error);
      toast.error(`Failed to delete tasks: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Template Push Functions
  const pushSelectedTemplates = async () => {
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one template');
      return;
    }
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    setPushingTemplates(true);
    try {
      const results = await AdminMilestoneService.pushSpecificTemplates(
        selectedStudents,
        selectedTemplates,
        { forceGenerate: true }
      );

      if (results.success) {
        toast.success(`Successfully pushed ${results.totalGenerated} tasks to ${results.studentsProcessed} students`);
        // Clear selections and refresh
        setSelectedTemplates([]);
        await loadStats();
        await loadStudents();
      } else {
        toast.error(`Failed to push templates: ${results.error}`);
      }
    } catch (error) {
      console.error('Error pushing templates:', error);
      toast.error('Failed to push templates to students');
    } finally {
      setPushingTemplates(false);
    }
  };

  const filteredTemplates = templates.filter(template => {
    // Apply search filter
    if (templateSearch && !template.title.toLowerCase().includes(templateSearch.toLowerCase()) &&
        !template.description?.toLowerCase().includes(templateSearch.toLowerCase())) {
      return false;
    }
    // Apply category filter
    if (templateCategoryFilter && template.category !== templateCategoryFilter) {
      return false;
    }
    // Apply phase filter
    if (templatePhaseFilter && template.phase !== templatePhaseFilter) {
      return false;
    }
    return true;
  });

  const getStudentPhase = (graduationYear) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    
    if (!graduationYear) return 'unknown';
    
    if (graduationYear === currentYear) {
      return currentMonth < 6 ? 'decision' : 'exploration';
    } else if (graduationYear === currentYear + 1) {
      if (currentMonth >= 8) return 'application';
      else if (currentMonth >= 3) return 'preparation';
      else return 'exploration';
    } else if (graduationYear === currentYear + 2) {
      return currentMonth >= 6 ? 'preparation' : 'exploration';
    }
    return 'exploration';
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold">Milestone Generation</h2>
        <p className="text-slate-600">Generate milestones for students</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Students</p>
              <p className="text-2xl font-bold">{stats.totalStudents}</p>
            </div>
            <Users className="h-8 w-8 text-azure-500" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">With Tasks</p>
              <p className="text-2xl font-bold">{stats.studentsWithMilestones}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-emerald-500" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Missing Tasks</p>
              <p className="text-2xl font-bold">{stats.missingMilestones.length}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-orange-500" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Tasks</p>
              <p className="text-2xl font-bold">{stats.totalMilestones}</p>
            </div>
            <Clock className="h-8 w-8 text-violet-500" />
          </div>
        </div>
      </div>

      {/* Automatic Trigger System */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
          Automatic Trigger System
        </h3>
        <div className="space-y-3 text-sm">
          <div className="bg-emerald-50 border-l-4 border-emerald-400 p-3 rounded">
            <div className="font-medium text-emerald-800 mb-1">🎯 Global Tasks Auto-Generation</div>
            <div className="text-emerald-700">Triggered when student starts College Journey (gets access to system)</div>
            <div className="text-xs text-emerald-600 mt-1">Location: college-journey-service.js</div>
          </div>
          
          <div className="bg-azure-50 border-l-4 border-azure-400 p-3 rounded">
            <div className="font-medium text-azure-800 mb-1">🎓 Immediate College Tasks Auto-Generation</div>
            <div className="text-azure-700">Triggered when college is added to student's Reach/Target/Safety grouping</div>
            <div className="text-xs text-azure-600 mt-1">Location: college-journey-service.js, EnhancedCollegeStrategy.jsx</div>
          </div>
          
          <div className="bg-violet-50 border-l-4 border-violet-400 p-3 rounded">
            <div className="font-medium text-violet-800 mb-1">📅 Deadline College Tasks Auto-Generation</div>
            <div className="text-violet-700">Triggered when student populates college data/deadlines + essays AND verifies accuracy</div>
            <div className="text-xs text-violet-600 mt-1">Location: EnhancedCollegeStrategy.jsx (handleVerifiedTaskGeneration)</div>
          </div>
          
          <div className="bg-orange-50 border-l-4 border-orange-400 p-3 rounded">
            <div className="font-medium text-orange-800 mb-1">🔄 Timeline Redistribution</div>
            <div className="text-orange-700">Available for students who fall behind - recalculates due dates for incomplete tasks</div>
            <div className="text-xs text-orange-600 mt-1">Status: Implemented but no student UI button added yet</div>
          </div>
          
          <div className="mt-4 p-3 bg-stone-50 rounded border">
            <div className="font-medium text-slate-800 mb-2">📋 Testing Interface Below</div>
            <div className="text-xs text-slate-600">
              The buttons below are for <strong>testing only</strong>. Production uses the automatic triggers above.
              Use these to manually test milestone generation and verify the automatic system is working.
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-4">
          <h3 className="font-semibold mb-2">Generate Milestones</h3>
          <div className="text-sm text-slate-600 mb-4 bg-stone-50 p-3 rounded">
            <div className="mb-1"><strong>Global Tasks:</strong> Universal tasks (FAFSA, transcripts, Common App)</div>
            <div><strong>College Tasks:</strong> College-specific tasks (supplements, essays, visits)</div>
          </div>
        </div>
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            <button
              onClick={selectAllMissing}
              className="px-4 py-2 border border-stone-300 rounded-lg hover:bg-stone-50 text-slate-700"
              disabled={generating}
            >
              Select All Missing
            </button>
            <button
              onClick={previewGeneration}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 flex items-center gap-2"
              disabled={generating || selectedStudents.length === 0}
            >
              <Eye className="h-4 w-4" />
              Preview
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => previewDelete('milestone')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                selectedStudents.length === 0 || deleting
                  ? 'bg-orange-300 text-orange-100 cursor-not-allowed'
                  : 'bg-orange-600 hover:bg-orange-700 text-white'
              }`}
              disabled={selectedStudents.length === 0 || deleting}
              title="Delete only milestone template tasks"
            >
              {deleting && deleteType === 'milestone' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete Milestone Tasks
            </button>
            <button
              onClick={() => previewDelete('all')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                selectedStudents.length === 0 || deleting
                  ? 'bg-red-300 text-red-100 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
              disabled={selectedStudents.length === 0 || deleting}
              title="Delete ALL tasks including deadline reminders, essays, and other auto-generated tasks"
            >
              {deleting && deleteType === 'all' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete ALL Tasks
            </button>
          </div>
        </div>

        {/* Generation Type Explanation */}
        <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 mb-4 text-sm">
          <div className="font-semibold mb-2 text-slate-800">Task Generation Types</div>
          <div className="grid md:grid-cols-3 gap-4 text-slate-700">
            <div>
              <strong className="text-emerald-700">Global Tasks:</strong> Universal tasks done once per student
            </div>
            <div>
              <strong className="text-azure-700">Immediate College Tasks:</strong> Strategic tasks when college added
            </div>
            <div>
              <strong className="text-violet-700">Deadline College Tasks:</strong> Application tasks tied to deadlines
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            <button
              onClick={() => generateGlobalMilestones()}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                generating 
                  ? 'bg-slate-400 cursor-not-allowed' 
                  : 'bg-slate-600 hover:bg-slate-700'
              } text-white`}
              disabled={generating || selectedStudents.length === 0}
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              Generate Global Tasks
            </button>
            <button
              onClick={() => generateCollegeMilestones()}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                generating 
                  ? 'bg-slate-400 cursor-not-allowed' 
                  : 'bg-slate-600 hover:bg-slate-700'
              } text-white`}
              disabled={generating || selectedStudents.length === 0}
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              Generate College Tasks
            </button>
            <button
              onClick={() => generateCollegeMilestonesDeadline()}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                generating 
                  ? 'bg-slate-400 cursor-not-allowed' 
                  : 'bg-slate-600 hover:bg-slate-700'
              } text-white`}
              disabled={generating || selectedStudents.length === 0}
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Clock className="h-4 w-4" />
              )}
              Generate Deadline Tasks
            </button>
          </div>
        </div>

        {/* Preview */}
        {preview && (
          <div className="mb-4 p-4 bg-azure-50 rounded-lg">
            <h4 className="font-medium mb-3">Generation Preview</h4>
            <div className="space-y-3">
              {preview.map(item => (
                <div key={item.studentId} className="bg-white p-3 rounded border border-azure-200">
                  <div className="font-medium text-sm mb-1">
                    {item.studentName}
                    {item.isRushTimeline && (
                      <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">
                        Rush Timeline
                      </span>
                    )}
                  </div>
                  
                  <div className="text-sm mb-2">
                    <span className="font-medium">{item.estimatedMilestones}</span> global tasks (done once per student)
                    {item.filteredCount > 0 && (
                      <span className="text-orange-600 text-xs ml-2">
                        ({item.filteredCount} time-sensitive tasks filtered out)
                      </span>
                    )}
                  </div>
                  
                  {Object.keys(item.categoryBreakdown || {}).length > 0 && (
                    <div className="text-xs text-slate-600 mb-2">
                      <strong>By Category:</strong> {Object.entries(item.categoryBreakdown).map(([cat, count], idx) => (
                        <span key={cat}>
                          {idx > 0 && ', '}
                          {count} {cat}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {Object.keys(item.phaseBreakdown || {}).length > 0 && (
                    <div className="text-xs text-stone-500 mb-2">
                      <strong>By Phase:</strong> {Object.entries(item.phaseBreakdown).map(([phase, count], idx) => (
                        <span key={phase}>
                          {idx > 0 && ', '}
                          {count} {phase}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="text-sm text-azure-600 mt-2">
                    <strong>Colleges:</strong> {item.colleges.length > 0 ? (
                      <span>{item.colleges.length} colleges ready for college-specific milestones</span>
                    ) : (
                      <span className="text-stone-500">No colleges added yet - only global milestones available</span>
                    )}
                  </div>
                </div>
              ))}
              <div className="text-xs text-stone-500 italic mt-3 bg-stone-100 p-2 rounded">
                <strong>Preview shows Global Tasks only.</strong> Use "Generate Global Tasks" for general tasks done once (FAFSA, transcripts, Common App), or "Generate College Tasks" for college-specific requirements (supplements, essays per school).
                {preview.some(item => item.filteredCount > 0) && (
                  <> Time-sensitive tasks are filtered out for students on rush timelines.</>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Push Specific Templates Section */}
        <div className="mt-6 pt-6 border-t border-stone-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold">Push Specific Templates to Selected Students</h3>
            <button
              onClick={() => setShowTemplatePush(!showTemplatePush)}
              className="flex items-center gap-2 px-3 py-1 rounded-lg hover:bg-stone-100 text-slate-600"
            >
              {showTemplatePush ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {showTemplatePush ? 'Hide' : 'Show'} Template Selector
            </button>
          </div>

          {showTemplatePush && (
            <div className="space-y-4">
              {/* Template Filters */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
                    <input
                      type="text"
                      placeholder="Search templates..."
                      value={templateSearch}
                      onChange={(e) => setTemplateSearch(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-stone-300 rounded-lg"
                    />
                  </div>
                </div>
                <select
                  value={templateCategoryFilter}
                  onChange={(e) => setTemplateCategoryFilter(e.target.value)}
                  className="px-3 py-2 border border-stone-300 rounded-lg"
                >
                  <option value="">All Categories</option>
                  <option value="application">Application</option>
                  <option value="essay">Essay</option>
                  <option value="testing">Testing</option>
                  <option value="financial_aid">Financial Aid</option>
                  <option value="profile">Profile</option>
                  <option value="research">Research</option>
                </select>
                <select
                  value={templatePhaseFilter}
                  onChange={(e) => setTemplatePhaseFilter(e.target.value)}
                  className="px-3 py-2 border border-stone-300 rounded-lg"
                >
                  <option value="">All Phases</option>
                  <option value="exploration">Exploration</option>
                  <option value="preparation">Preparation</option>
                  <option value="application">Application</option>
                  <option value="decision">Decision</option>
                </select>
              </div>

              {/* Selected Count and Actions */}
              <div className="flex justify-between items-center">
                <div className="text-sm text-slate-600">
                  {selectedTemplates.length} template{selectedTemplates.length !== 1 ? 's' : ''} selected,{' '}
                  {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedTemplates(filteredTemplates.map(t => t.id))}
                    className="px-3 py-1 text-sm border border-stone-300 rounded hover:bg-stone-50"
                  >
                    Select All Visible
                  </button>
                  <button
                    onClick={() => setSelectedTemplates([])}
                    className="px-3 py-1 text-sm border border-stone-300 rounded hover:bg-stone-50"
                  >
                    Clear Selection
                  </button>
                  <button
                    onClick={pushSelectedTemplates}
                    disabled={pushingTemplates || selectedTemplates.length === 0 || selectedStudents.length === 0}
                    className={`px-4 py-1 text-sm rounded flex items-center gap-2 ${
                      pushingTemplates || selectedTemplates.length === 0 || selectedStudents.length === 0
                        ? 'bg-stone-300 text-stone-500 cursor-not-allowed'
                        : 'bg-azure-600 text-white hover:bg-azure-700'
                    }`}
                  >
                    {pushingTemplates ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Push Templates
                  </button>
                </div>
              </div>

              {/* Template List */}
              <div className="max-h-64 overflow-y-auto border border-stone-200 rounded-lg">
                {filteredTemplates.length === 0 ? (
                  <div className="p-4 text-center text-stone-500">
                    No templates found matching your filters
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 sticky top-0">
                      <tr className="border-b">
                        <th className="text-left p-2 w-10">
                          <input
                            type="checkbox"
                            checked={filteredTemplates.length > 0 && filteredTemplates.every(t => selectedTemplates.includes(t.id))}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTemplates([...new Set([...selectedTemplates, ...filteredTemplates.map(t => t.id)])]);
                              } else {
                                setSelectedTemplates(selectedTemplates.filter(id => !filteredTemplates.find(t => t.id === id)));
                              }
                            }}
                          />
                        </th>
                        <th className="text-left p-2">Title</th>
                        <th className="text-left p-2">Category</th>
                        <th className="text-left p-2">Phase</th>
                        <th className="text-left p-2">Priority</th>
                        <th className="text-left p-2">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTemplates.map(template => (
                        <tr key={template.id} className="border-b hover:bg-stone-50">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedTemplates.includes(template.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTemplates([...selectedTemplates, template.id]);
                                } else {
                                  setSelectedTemplates(selectedTemplates.filter(id => id !== template.id));
                                }
                              }}
                            />
                          </td>
                          <td className="p-2">
                            <div>
                              <div className="font-medium">{template.title}</div>
                              {template.description && (
                                <div className="text-xs text-stone-500 truncate max-w-md">{template.description}</div>
                              )}
                            </div>
                          </td>
                          <td className="p-2">
                            <span className={`px-2 py-1 text-xs rounded ${
                              template.category === 'essay' ? 'bg-violet-100 text-violet-700' :
                              template.category === 'application' ? 'bg-azure-100 text-azure-700' :
                              template.category === 'research' ? 'bg-emerald-100 text-emerald-700' :
                              template.category === 'profile' ? 'bg-indigo-100 text-indigo-700' :
                              template.category === 'financial_aid' ? 'bg-emerald-100 text-emerald-700' :
                              template.category === 'testing' ? 'bg-orange-100 text-orange-700' :
                              'bg-stone-100 text-slate-700'
                            }`}>
                              {template.category}
                            </span>
                          </td>
                          <td className="p-2">
                            <span className="px-2 py-1 text-xs rounded bg-stone-100 text-slate-700">
                              {template.phase}
                            </span>
                          </td>
                          <td className="p-2">
                            <span className={`px-2 py-1 text-xs rounded ${
                              template.priority === 'critical' ? 'bg-red-100 text-red-700' :
                              template.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                              template.priority === 'medium' ? 'bg-azure-100 text-azure-700' :
                              'bg-stone-100 text-slate-700'
                            }`}>
                              {template.priority}
                            </span>
                          </td>
                          <td className="p-2">
                            <span className={`px-2 py-1 text-xs rounded ${
                              template.is_college_specific ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-700'
                            }`}>
                              {template.is_college_specific ? 'College' : 'Global'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Student List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-4 text-stone-500">Loading students...</div>
          ) : (
            students.map(student => (
              <div
                key={student.id}
                className={`flex items-center justify-between p-3 border rounded-lg ${
                  selectedStudents.includes(student.id) ? 'bg-azure-50 border-azure-300' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStudents([...selectedStudents, student.id]);
                      } else {
                        setSelectedStudents(selectedStudents.filter(id => id !== student.id));
                      }
                    }}
                  />
                  <div>
                    <div className="font-medium">{student.full_name || student.name || 'Unnamed'}</div>
                    <div className="text-sm text-slate-600">
                      {student.email} | Class of {student.graduation_year || 'Unknown'}
                    </div>
                    <div className="text-xs text-stone-500">
                      {student.milestoneCount} tasks | {student.collegeCount} colleges | 
                      Phase: <span className="font-medium">{getStudentPhase(student.graduation_year)}</span>
                    </div>
                    {student.milestoneCount === 0 && (
                      <div className="text-xs text-orange-600 mt-1">
                        ⚠️ No tasks generated yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                deleteType === 'milestone' ? 'bg-orange-100' : 'bg-red-100'
              }`}>
                <AlertCircle className={`w-6 h-6 ${
                  deleteType === 'milestone' ? 'text-orange-600' : 'text-red-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {deleteType === 'milestone' ? 'Delete Milestone Tasks' : 'Delete ALL Tasks'}
                </h3>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>

            <div className="mb-6">
              {/* Task Type Breakdown */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                <div className="font-semibold text-sm text-slate-800 mb-3">
                  {deleteType === 'milestone' ? 'Milestone Tasks to Delete:' : 'All Tasks to Delete:'}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white p-3 rounded border">
                    <div className="text-slate-600">Milestone Tasks</div>
                    <div className="text-2xl font-bold text-slate-900">{deletePreview.milestoneCount}</div>
                    <div className="text-xs text-slate-500">template + subtask</div>
                  </div>
                  {deleteType === 'all' && (
                    <>
                      <div className="bg-white p-3 rounded border">
                        <div className="text-slate-600">Deadline Reminders</div>
                        <div className="text-2xl font-bold text-orange-600">{deletePreview.deadlineCount}</div>
                        <div className="text-xs text-slate-500">auto-generated 🚨 🔥 ⚠️</div>
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <div className="text-slate-600">Essay Tasks</div>
                        <div className="text-2xl font-bold text-violet-600">{deletePreview.essayCount}</div>
                        <div className="text-xs text-slate-500">essay workflow</div>
                      </div>
                      <div className="bg-white p-3 rounded border">
                        <div className="text-slate-600">Other Tasks</div>
                        <div className="text-2xl font-bold text-azure-600">{deletePreview.otherCount}</div>
                        <div className="text-xs text-slate-500">recommendations, etc.</div>
                      </div>
                    </>
                  )}
                </div>
                <div className={`mt-3 pt-3 border-t text-center ${
                  deleteType === 'milestone' ? 'border-orange-200' : 'border-red-200'
                }`}>
                  <div className="text-sm text-slate-600">Total Tasks to Delete</div>
                  <div className={`text-3xl font-bold ${
                    deleteType === 'milestone' ? 'text-orange-600' : 'text-red-600'
                  }`}>
                    {deleteType === 'milestone' ? deletePreview.milestoneCount : deletePreview.totalCount}
                  </div>
                </div>
              </div>

              {/* Student List */}
              <div className="mb-3">
                <p className="text-sm text-gray-700 mb-2">
                  Deleting tasks for <strong>{selectedStudents.length}</strong> student{selectedStudents.length !== 1 ? 's' : ''}:
                </p>
                <ul className="text-sm text-gray-600 max-h-32 overflow-y-auto bg-gray-50 p-3 rounded border">
                  {deletePreview.students.map(student => (
                    <li key={student.id} className="flex justify-between py-1">
                      <span>{student.name}</span>
                      <span className="font-medium">
                        {deleteType === 'milestone' ? student.milestoneCount : student.totalCount} tasks
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Warning */}
              <div className={`p-3 rounded border-l-4 ${
                deleteType === 'milestone'
                  ? 'bg-orange-50 border-orange-400'
                  : 'bg-red-50 border-red-400'
              }`}>
                <p className={`text-sm font-medium ${
                  deleteType === 'milestone' ? 'text-orange-800' : 'text-red-800'
                }`}>
                  ⚠️ Warning
                </p>
                <p className={`text-sm mt-1 ${
                  deleteType === 'milestone' ? 'text-orange-700' : 'text-red-700'
                }`}>
                  {deleteType === 'milestone' ? (
                    <>This will delete <strong>only milestone template tasks</strong>. Deadline reminders, essay tasks, and other auto-generated tasks will remain.</>
                  ) : (
                    <>This will <strong>permanently delete ALL tasks</strong> including milestone templates, deadline reminders, essay workflows, and all other timeline items. This is a complete reset.</>
                  )}
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletePreview(null);
                }}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={deleteAllTasksForStudents}
                disabled={deleting}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  deleteType === 'milestone'
                    ? 'bg-orange-600 hover:bg-orange-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    {deleteType === 'milestone' ? 'Delete Milestone Tasks' : 'Delete ALL Tasks'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}